import { EventEmitter, Injectable, DOCUMENT, inject } from '@angular/core';
import { WINDOW } from '@shared/dom';
import { clearTheme, selectTheme, setTheme, StoreService } from '@shared/store';

export enum ColorScheme {
  /** Use dark scheme */
  Dark = 'dark',
  /** Use light scheme */
  Light = 'light',
}

@Injectable({
  providedIn: 'root',
})
export class ColorSchemeManagerService {
  private readonly window = inject<Window>(WINDOW);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly store = inject(StoreService);

  /**
   * A new scheme setting has been specified by the user.
   */
  public readonly schemeChanged = new EventEmitter<ColorScheme | null>();

  private get root(): HTMLHtmlElement {
    return this.document.documentElement as HTMLHtmlElement;
  }

  public init(): void {
    this.loadScheme();
  }

  public setScheme(scheme: ColorScheme | null): void {
    if (scheme) {
      this.store.dispatch(setTheme({ theme: scheme }));
    } else {
      this.store.dispatch(clearTheme());
    }
    this.loadScheme();
    this.schemeChanged.emit(scheme);
  }

  /**
   * Get the currently stored scheme for the user.
   *
   * If no color scheme has been configured before (e.g. this is a first-time visitor) this will be null.
   */
  public getSavedScheme(): ColorScheme | undefined {
    return this.store.select(selectTheme());
  }

  /**
   * Load and apply the current scheme, determined by user or browser preferences.
   */
  private loadScheme(): void {
    const current = this.getSavedScheme();
    if (current) {
      this.applyScheme(current);
    } else {
      const preferred = this.getAgentPreferredScheme();
      this.applyScheme(preferred);
    }
  }

  /**
   * Apply a color scheme to the HTML element.
   * @param scheme The color scheme to apply
   */
  private applyScheme(scheme: ColorScheme): void {
    switch (scheme) {
      case ColorScheme.Dark:
        this.root.setAttribute('dark', 'true');
        break;
      case ColorScheme.Light:
        this.root.setAttribute('dark', 'false');
        break;
    }
  }

  /**
   * Get the user agent's preferred color scheme
   */
  private getAgentPreferredScheme(): ColorScheme {
    return this.agentPrefersDarkScheme() ? ColorScheme.Dark : ColorScheme.Light;
  }

  /**
   * Get whether the user agent prefers dark scheme
   */
  private agentPrefersDarkScheme(): boolean {
    const matcher = this.window.matchMedia('(prefers-color-scheme: dark)');
    return matcher.matches;
  }
}
